/**
 * Centralized status colors for the design system.
 * These colors correspond to CSS variables defined in common.scss.
 */

import type { StatusValue } from 'types/spherePoints';
import type { StatusBadgeStatus } from '../components/atoms/StatusBadge/StatusBadgeAtom';

/** Extended status type including lifecycle states */
export type ExtendedStatusValue = StatusValue | 'not-started' | 'completed';

/**
 * Status color hex values - use for JS/canvas/SVG where CSS vars aren't available
 */
export const STATUS_COLORS: Record<ExtendedStatusValue, string> = {
  on_track: '#10B981',
  at_risk: '#F59E0B',
  needs_attention: '#EA580C',
  critical: '#EF4444',
  blocked: '#DC2626',
  unknown: '#9CA3AF',
  'not-started': '#9CA3AF',
  completed: '#10B981',
};

/**
 * CSS variable names for status colors - use in React style props
 */
export const STATUS_CSS_VARS: Record<ExtendedStatusValue, string> = {
  on_track: 'var(--status-on-track)',
  at_risk: 'var(--status-at-risk)',
  needs_attention: 'var(--status-needs-attention)',
  critical: 'var(--status-critical)',
  blocked: 'var(--status-blocked)',
  unknown: 'var(--status-unknown)',
  'not-started': 'var(--status-not-started)',
  completed: 'var(--status-completed)',
};

/**
 * Status labels for display
 */
export const STATUS_LABELS: Record<ExtendedStatusValue, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  needs_attention: 'Needs Attention',
  critical: 'Critical',
  blocked: 'Blocked',
  unknown: 'Unknown',
  'not-started': 'Not Started',
  completed: 'Completed',
};

/**
 * Get the hex color for a status value
 */
export function getStatusColor(status: ExtendedStatusValue | undefined): string {
  return STATUS_COLORS[status || 'unknown'] || STATUS_COLORS.unknown;
}

/**
 * Get the CSS variable for a status value
 */
export function getStatusCssVar(status: ExtendedStatusValue | undefined): string {
  return STATUS_CSS_VARS[status || 'unknown'] || STATUS_CSS_VARS.unknown;
}

/**
 * Get the label for a status value
 */
export function getStatusLabel(status: ExtendedStatusValue | undefined): string {
  return STATUS_LABELS[status || 'unknown'] || STATUS_LABELS.unknown;
}

/**
 * Get status from a Sphere object
 */
export function getStatusFromSphere(sphere: { status?: { currentValue?: { status?: StatusValue } } } | null | undefined): StatusValue {
  return sphere?.status?.currentValue?.status || 'unknown';
}

/**
 * Get a localized label for a StatusValue using the shared statusVocabulary.* i18n keys.
 * Pass the `t` function from useTranslation() as the second argument.
 */
export function getLocalizedStatusLabel(
  status: StatusValue | undefined,
  t: (key: string) => string,
): string {
  const key = status || 'unknown';
  return t(`statusVocabulary.${key}`);
}

/** Glyph for a status badge; the badge and any control that must read like one share it. */
export const getIconClass = (status: StatusBadgeStatus): string => {
  switch (status) {
    case 'active':
      return 'pi pi-check-circle';
    case 'overdue':
      return 'pi pi-exclamation-triangle';
    case 'draft':
      return 'pi pi-file-edit';
    case 'error':
      return 'pi pi-times-circle';
    case 'pending':
      return 'pi pi-clock';
    case 'deactivated':
      return 'pi pi-minus-circle';
    case 'observing':
      return 'pi pi-eye';
    case 'paused':
      return 'pi pi-pause-circle';
    case 'approach':
      return 'pi pi-box';
    case 'channel':
      return 'pi pi-link';
    case 'manual':
      return 'pi pi-user';
    case 'fork':
      return 'pi pi-code-branch';
    case 'conformance_pass':
      return 'pi pi-check-circle';
    case 'conformance_fail':
      return 'pi pi-times-circle';
    case 'conformance_stale':
      return 'pi pi-history';
    case 'unknown':
    default:
      return 'pi pi-question-circle';
  }
};

/** Glyph for a health value, so a status option reads by shape and word, never by colour alone. */
export const getStatusValueIconClass = (status: StatusValue | undefined): string => {
  switch (status) {
    case 'on_track':
      return 'pi pi-check-circle';
    case 'at_risk':
      return 'pi pi-exclamation-triangle';
    case 'needs_attention':
      return 'pi pi-exclamation-circle';
    case 'critical':
      return 'pi pi-times-circle';
    case 'blocked':
      return 'pi pi-ban';
    case 'unknown':
    default:
      return getIconClass('unknown');
  }
};
